import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../containers/UseAuth';
import { getPublishersList } from '../../../../../services/screensService';

const ScreenPublisher = ({ publisher, isChecked, onToggle }) => {
    const handleChange = (event) => {
        onToggle(publisher, event.target.checked);
    };

    return (
        <div className="flex items-center justify-between my-2">
            <div className="flex items-center">
                <input
                    checked={isChecked}
                    onChange={handleChange}
                    name={`screen-publisher-${publisher.id}`}
                    id={`screen-publisher-${publisher.id}`}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-0 border-gray-300 rounded"
                />
                <label
                    htmlFor={`screen-publisher-${publisher.id}`}
                    className="ml-2 flex items-center text-sm text-gray-600">
                    <span>{publisher.name}</span>
                    <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-400">
                        {publisher.count ?? 0}
                    </span>
                </label>
            </div>
        </div>
    );
};

ScreenPublisher.propTypes = {
    publisher: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
    }).isRequired,
    isChecked: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

const ScreenPublisherList = ({
    selectedPublishers = [],
    availablePublisherIds = [],
    availablePublisherCounts = [],
    screensForCounts = null,
    onChange,
}) => {
    const { t } = useTranslation();
    const auth = useAuth();
    const canFilterByPublisher =
        (auth?.isAdmin?.() ?? false) || (auth?.isSupervising?.() ?? false);
    const [publishers, setPublishers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!canFilterByPublisher) return undefined;

        let active = true;
        const load = async () => {
            setLoading(true);
            try {
                const { results } = await getPublishersList({
                    limit: 500,
                    orderBy: 'name',
                    order: 'asc',
                });
                if (!active) return;
                setPublishers(Array.isArray(results) ? results : []);
            } catch (error) {
                if (active) {
                    setPublishers([]);
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            active = false;
        };
    }, [canFilterByPublisher]);

    useEffect(() => {
        if (!canFilterByPublisher && selectedPublishers.length && onChange) {
            onChange([]);
        }
    }, [canFilterByPublisher, selectedPublishers, onChange]);

    const normalizedAvailablePublisherIds = useMemo(
        () =>
            new Set(
                (availablePublisherIds || [])
                    .map((id) => String(id ?? '').trim())
                    .filter(Boolean)
            ),
        [availablePublisherIds]
    );
    // When screensForCounts is provided (e.g. "show selected" mode), compute
    // counts by matching screen.publisher (name string) to the loaded publishers
    // list, bypassing the server-side availablePublisherIds/Counts entirely.
    const publisherCountLookup = useMemo(() => {
        const map = new Map();

        if (Array.isArray(screensForCounts) && screensForCounts.length > 0) {
            const nameToId = new Map(
                publishers
                    .filter((p) => p?.id != null && p?.name)
                    .map((p) => [p.name.toLowerCase(), String(p.id)])
            );
            screensForCounts.forEach((screen) => {
                const pubName =
                    typeof screen?.publisher === 'string'
                        ? screen.publisher
                        : screen?.publisher?.name ?? '';
                if (!pubName) return;
                const id = nameToId.get(pubName.toLowerCase());
                if (!id) return;
                map.set(id, (map.get(id) ?? 0) + 1);
            });
            return map;
        }

        (availablePublisherCounts || []).forEach((item) => {
            const key = String(item?.id ?? '').trim();
            const count = Number(item?.count ?? 0);
            if (!key || !Number.isFinite(count)) return;
            map.set(key, count);
        });
        return map;
    }, [screensForCounts, publishers, availablePublisherCounts]);

    const publishersToDisplay = useMemo(() => {
        const map = new Map();

        // In screensForCounts mode show all loaded publishers so the user can
        // keep adding filters — sort brings the relevant ones to the top.
        // Otherwise restrict by server-side availablePublisherIds when present.
        const useScreensMode =
            Array.isArray(screensForCounts) && screensForCounts.length > 0;
        const shouldRestrictByAvailable =
            !useScreensMode && normalizedAvailablePublisherIds.size > 0;

        publishers.forEach((publisher) => {
            if (!publisher || publisher.id == null) return;
            const key = String(publisher.id);
            if (
                shouldRestrictByAvailable &&
                !normalizedAvailablePublisherIds.has(key)
            )
                return;
            map.set(key, {
                ...publisher,
                count: publisherCountLookup.get(key) ?? 0,
            });
        });

        // The catalog is capped (limit 500), so a publisher with matching
        // screens can be missing from it. Fall back to the name the API sends
        // with each count so no available publisher is hidden.
        if (!useScreensMode) {
            (availablePublisherCounts || []).forEach((item) => {
                const key = String(item?.id ?? '').trim();
                if (!key || map.has(key) || !item?.name) return;
                map.set(key, {
                    id: item.id,
                    name: item.name,
                    count: publisherCountLookup.get(key) ?? 0,
                });
            });
        }

        (selectedPublishers || []).forEach((publisher) => {
            if (!publisher || publisher.id == null) return;
            const key = String(publisher.id);
            if (!map.has(key)) {
                map.set(key, {
                    ...publisher,
                    count: publisherCountLookup.get(key) ?? 0,
                });
            }
        });

        return Array.from(map.values()).sort((a, b) => {
            const aHas = (a.count ?? 0) > 0 ? 0 : 1;
            const bHas = (b.count ?? 0) > 0 ? 0 : 1;
            if (aHas !== bHas) return aHas - bHas;
            const nameA = (a?.name || '').toLowerCase();
            const nameB = (b?.name || '').toLowerCase();
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
        });
    }, [
        publishers,
        selectedPublishers,
        normalizedAvailablePublisherIds,
        publisherCountLookup,
        screensForCounts,
        availablePublisherCounts,
    ]);

    const selectedIds = useMemo(() => {
        const set = new Set();
        (selectedPublishers || []).forEach((publisher) => {
            if (publisher?.id != null) {
                set.add(String(publisher.id));
            }
        });
        return set;
    }, [selectedPublishers]);

    const handleToggle = (publisher, checked) => {
        if (!onChange || !publisher?.id) return;
        const id = String(publisher.id);

        if (checked) {
            if (selectedIds.has(id)) return;
            onChange([...(selectedPublishers || []), publisher]);
        } else {
            onChange(
                (selectedPublishers || []).filter(
                    (item) => String(item?.id) !== id
                )
            );
        }
    };

    if (!canFilterByPublisher) return null;

    return publishersToDisplay.length > 0 || loading ? (
        <div className="font-light pt-2 w-40 w-full">
            <div className="title font-light py-2 shadow-sm">
                {t(
                    'sections.campaigns.new-campaign.wizard-screens.filters.screenProviders-label'
                )}
            </div>
            <div className="max-h-40 overflow-y-scroll overflow-hidden">
                {loading && !publishersToDisplay.length ? (
                    <div className="text-xs text-gray-400 py-2">
                        {t('common.words.loading')}
                    </div>
                ) : null}
                {publishersToDisplay.map((publisher) => (
                    <ScreenPublisher
                        key={publisher.id ?? publisher.name}
                        publisher={publisher}
                        isChecked={selectedIds.has(String(publisher.id))}
                        onToggle={handleToggle}
                    />
                ))}
            </div>
        </div>
    ) : null;
};

ScreenPublisherList.propTypes = {
    selectedPublishers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
        })
    ),
    availablePublisherIds: PropTypes.array,
    availablePublisherCounts: PropTypes.array,
    screensForCounts: PropTypes.array,
    onChange: PropTypes.func,
};

export default ScreenPublisherList;
